'use client';
import Link from 'next/link';
import { useRouter, useParams } from 'next/navigation';
import { useState, useEffect, useRef, useCallback } from 'react';
import { formatArabicDate, truncateText } from '@/app/funcs/funcs';
import {
    getData,
    getDataWithPagination,
    isLoggedIn,
    fetchAuthorInfo,
    fetchAuthorPostsCount,
} from '@/app/funcs/api';
import AddNewPost from '@/app/components/CMS/add-new';
import { CategoriesFilter } from '@/app/components/CMS/posts';
import { toast } from 'react-toastify';
// export default async function dataList() {
export default function itemType() {
    // const params = useParams();
    const postType = 'designreview';
    const [data, setData] = useState([]);
    const [allData, setAllData] = useState([]); // Store all fetched posts
    const [error, setError] = useState();
    const router = useRouter();
    const [pagetitle, setPageTitle] = useState();
    // const [postType, setpostType] = useState();
    const [showAddNew, setShowAddNew] = useState(false);
    const [userLoggedIn, setUserLoggedIn] = useState(false);
    const [authorsData, setAuthorsData] = useState({});
    const [selectedCategory, setSelectedCategory] = useState(null); // Category filter state

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPosts, setTotalPosts] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [perPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [useInfiniteScroll, setUseInfiniteScroll] = useState(true);

    // Infinite scroll ref
    const observerRef = useRef();

    useEffect(() => {
        // Check if user is logged in using existing function
        setUserLoggedIn(!!isLoggedIn());

        const arabicTitleMap = {
            article: 'المقالات',
            articles: 'المقالات',
            plugins: 'الإضافات',
            templates: 'القوالب',
            challenge: 'التحديات',
            podcast: 'البودكاست',
            community: 'المجتمع',
            items: 'العناصر',
            quote: 'الاقتباسات',
            jobs: 'الوظائف',
            designreview: 'مراجعة التصاميم',
        };

        const arabicTitle = arabicTitleMap[postType] || postType; // fallback to original if not found
        setPageTitle(arabicTitle);

        // Reset pagination state when type changes
        setCurrentPage(1);
        setData([]);
        setTotalPosts(0);
        setTotalPages(0);
        setHasMore(true);

        // Fetch first page of posts
        fetchPosts(postType, 1);
    }, [postType]);

    // Cleanup intersection observer on unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    // Function to fetch posts with pagination

    const fetchPosts = async (postType, page, append = false) => {
        try {
            setLoading(true);
            const response = await getDataWithPagination(
                postType,
                page,
                perPage,
                '_embed'
            );

            let postsToSet = append ? [...allData, ...response.data] : response.data;

            if (!append) {
                setAllData(postsToSet);
            } else {
                setAllData((prev) => [...prev, ...response.data]);
            }

            setTotalPosts(response.total);
            setTotalPages(response.totalPages);
            setCurrentPage(response.currentPage);
            setHasMore(response.currentPage < response.totalPages);

            // Fetch author data for new posts
            fetchAuthorsData(response.data, postType);
        } catch (error) {
            console.error('Error fetching posts:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
            });

            // Fallback to original getData function
            try {
                const fallbackData = await getData(postType, '_embed');

                let postsToSet = append ? [...allData, ...fallbackData] : fallbackData;

                if (!append) {
                    setAllData(postsToSet);
                } else {
                    setAllData((prev) => [...prev, ...fallbackData]);
                }

                // Set default pagination values
                setTotalPosts(fallbackData.length);
                setTotalPages(1);
                setCurrentPage(1);
                setHasMore(false);

                // Fetch author data for fallback posts
                fetchAuthorsData(fallbackData, postType);
            } catch (fallbackError) {
                console.error('Fallback also failed:', fallbackError);

                if (!append) {
                    setError(`حدث خطأ في تحميل البيانات: ${error.message}`);
                } else {
                    toast.error('حدث خطأ في تحميل المزيد من المشاركات');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle category selection
    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        setCurrentPage(1);

        if (category) {
            // Filter posts by selected category
            const filtered = allData.filter(
                (post) =>
                    post.categories &&
                    Array.isArray(post.categories) &&
                    post.categories.includes(category.id)
            );
            setData(filtered);
            setTotalPosts(filtered.length);
            setTotalPages(1);
            setHasMore(false);
        } else {
            // Show all posts
            setData(allData);
            setTotalPosts(allData.length);
            setTotalPages(1);
            setHasMore(false);
        }
    };

    // Apply category filter when allData changes
    useEffect(() => {
        if (selectedCategory) {
            const filtered = allData.filter(
                (post) =>
                    post.categories &&
                    Array.isArray(post.categories) &&
                    post.categories.includes(selectedCategory.id)
            );
            setData(filtered);
            setTotalPosts(filtered.length);
            setTotalPages(1);
            setHasMore(false);
        } else {
            setData(allData);
            setTotalPosts(allData.length);
            setTotalPages(1);
            setHasMore(false);
        }
    }, [allData, selectedCategory]);

    // Load more posts function
    const loadMorePosts = () => {
        if (!loading && hasMore) {
            fetchPosts(postType, currentPage + 1, true);
        }
    };

    // Infinite scroll callback
    const lastPostElementRef = useCallback(
        (node) => {
            if (loading) return;
            if (observerRef.current) observerRef.current.disconnect();

            observerRef.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    loadMorePosts();
                }
            });

            if (node) observerRef.current.observe(node);
        },
        [loading, hasMore]
    );

    // Update URL when page changes
    useEffect(() => {
        if (currentPage > 1) {
            const url = new URL(window.location);
            url.searchParams.set('page', currentPage);
            window.history.replaceState({}, '', url);
        } else {
            const url = new URL(window.location);
            url.searchParams.delete('page');
            window.history.replaceState({}, '', url);
        }
    }, [currentPage]);

    // Check for page parameter in URL on mount
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const pageParam = urlParams.get('page');
            if (pageParam && !isNaN(parseInt(pageParam))) {
                const page = parseInt(pageParam);
                if (page > 1 && postType) {
                    setCurrentPage(page);
                    fetchPosts(postType, page);
                }
            }
        }
    }, []);

    // Test API connectivity
    useEffect(() => {
        if (postType) {
            // Test with a simple request to see if the API is working
            fetch(
                `${process.env.NEXT_PUBLIC_BASE_URL}api/wp/v2/${postType}?per_page=1`
            )
                .then((response) => {
                    return response.json();
                })
                .then((data) => { })
                .catch((error) => { });
        }
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')
                return;

            if (e.key === 'ArrowDown' && hasMore && !loading) {
                e.preventDefault();
                loadMorePosts();
            } else if (e.key === 'Home') {
                e.preventDefault();
                if (currentPage > 1) {
                    setCurrentPage(1);
                    fetchPosts(postType, 1);
                }
            } else if (e.key === 'End' && totalPages > 1) {
                e.preventDefault();
                if (currentPage < totalPages) {
                    setCurrentPage(totalPages);
                    fetchPosts(postType, totalPages);
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [hasMore, loading, currentPage, totalPages, postType]);

    // Function to fetch author data for all posts
    const fetchAuthorsData = async (posts, postType) => {
        const authors = {};

        for (const post of posts) {
            if (post.author && !authors[post.author]) {
                try {
                    const [authorInfo, postsCount] = await Promise.all([
                        fetchAuthorInfo(post.author),
                        fetchAuthorPostsCount(post.author, postType),
                    ]);

                    authors[post.author] = {
                        ...authorInfo,
                        postsCount,
                    };
                } catch (error) {
                    console.error('Error fetching author data:', error);
                    authors[post.author] = {
                        name: 'مستخدم غير معروف',
                        postsCount: 0,
                    };
                }
            }
        }

        setAuthorsData(authors);
    };

    const handleAddNewClick = () => {
        if (!userLoggedIn) {
            toast.error('لابد ان تسجل الدخول لتتمكن من المشاركة');
            return;
        }
        setShowAddNew(!showAddNew);
    };

    // Author Info Component
    const AuthorInfo = ({ authorId, postType }) => {
        const author = authorsData[authorId];

        if (!author) {
            return (
                <div className="d-flex align-items-center mt-2">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">جاري التحميل...</span>
                    </div>
                    <small className="text-muted">جاري تحميل معلومات الكاتب...</small>
                </div>
            );
        }
        const authorName =
            author.name ||
            author.user_display_name ||
            author.user_login ||
            author.name?.toLowerCase().replace(/\s+/g, '');
        const authorSlug =
            author.slug ||
            author.user_login ||
            author.name?.toLowerCase().replace(/\s+/g, '');
        const profileUrl = authorSlug ? `/@${authorSlug}` : '#';
        const AuthorAvatar = author?.acf?.avatar_id?.url
            ? author.acf?.avatar_id?.sizes?.thumbnail
            : author?.avatar_urls
                ? author?.avatar_urls[96]
                : null;

        return (
            <div className="d-flex align-items-center mt-2 rounded-5 p-2 w-fit border">
                {/* {JSON.stringify(author)} */}
                {AuthorAvatar ? (
                    <img
                        src={AuthorAvatar}
                        alt={author.name}
                        className="rounded-circle me-2"
                        width="24"
                        height="24"
                    />
                ) : (
                    <div
                        className="bg-secondary rounded-circle me-2 d-flex align-items-center justify-content-center"
                        style={{ width: '24px', height: '24px' }}
                    >
                        <i
                            className="bi bi-person text-white"
                            style={{ fontSize: '12px' }}
                        ></i>
                    </div>
                )}
                <Link href={profileUrl} className="text-decoration-none">
                    {/* <small className="text-dark fw-bold">@{authorName}</small> */}
                    <small className="text-dark fw-bold">{authorName}</small>
                </Link>
                {/* <small className="text-muted ms-2">•</small> */}
                <small className="text-muted ms-2">
                    ( {author.postsCount} ) مشاركة
                </small>
            </div>
        );
    };

    return (
        <>
            {!data && error && (
                <div className="mx-auto my-5 bg-white text-center col-auto border rounded-4 py-3 px-4">
                    <div className="text-danger mb-3">
                        <i className="bi bi-exclamation-triangle fs-1"></i>
                        <p className="mt-2">{error}</p>
                    </div>
                    <button
                        className="btn btn-outline-primary"
                        onClick={() => {
                            setError(null);
                            fetchPosts(postType, 1);
                        }}
                    >
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        إعادة المحاولة
                    </button>
                </div>
            )}

            {!data && !error && (
                <div className="mx-auto my-5 bg-white text-center col-auto border rounded-4 py-3 px-4">
                    جار تحميل البيانات ...
                    <div className="spinner-border" role="status">
                        <span className="sr-only"></span>
                    </div>
                </div>
            )}
            {data && (
                <div className="container py-4 px-2 col-12 col-xl-8 max-w-xl mx-auto bg-white rounded-4 p-md-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h1 className="pb-0 pageTitle mb-0"> {pagetitle} </h1>
                        <button
                            className="btn btn-dark"
                            onClick={handleAddNewClick}
                            title="إضافة جديد"
                        >
                            <i className="bi bi-plus-lg"></i> طلب مراجعة تصميم
                        </button>
                    </div>

                    {postType && (
                        <>
                            <CategoriesFilter
                                onCategorySelect={handleCategorySelect}
                                selectedCategory={selectedCategory}
                                postType={postType}
                                allData={allData}
                                totalPosts={totalPosts}
                            />
                        </>
                    )}

                    {!postType && (
                        <div className="mb-2 small text-warning">
                            ⚠️ Waiting for post type to be determined...
                        </div>
                    )}

                    {/* Posts count and pagination info */}
                    {data && data.length > 0 && (
                        // <div className="d-flex justify-content-between align-items-center mb-3 p-3 pagination-info bg-light  rounded-3">
                        <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light  rounded-3">
                            <small className="text-muted">
                                {selectedCategory ? (
                                    <>
                                        عدد {pagetitle} في <strong>{selectedCategory.name}</strong>{' '}
                                        :{' '}
                                        <span className="posts-count-badge">
                                            {totalPosts || 'جاري التحميل...'}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        عدد {pagetitle} :{' '}
                                        <span className="posts-count-badge">
                                            {totalPosts || 'جاري التحميل...'}
                                        </span>
                                    </>
                                )}
                            </small>
                            <div className="d-flex align-items-center gap-3">
                                <small className="text-muted">
                                    الصفحة <strong>{currentPage}</strong> من{' '}
                                    <strong>{totalPages || '...'}</strong>
                                </small>
                                <div className="form-check form-switch infinite-scroll-toggle">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="infiniteScrollToggle"
                                        checked={useInfiniteScroll}
                                        onChange={(e) => setUseInfiniteScroll(e.target.checked)}
                                    />
                                    <label
                                        className="form-check-label small"
                                        htmlFor="infiniteScrollToggle"
                                    >
                                        التمرير التلقائي
                                    </label>
                                </div>
                                <div className="dropdown">
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                        title="اختصارات لوحة المفاتيح"
                                    >
                                        <i className="bi bi-question-circle"></i>
                                    </button>
                                    <ul className="dropdown-menu">
                                        <li>
                                            <h6 className="dropdown-header">
                                                اختصارات لوحة المفاتيح
                                            </h6>
                                        </li>
                                        <li>
                                            <span className="dropdown-item-text">
                                                <kbd>↓</kbd> تحميل المزيد
                                            </span>
                                        </li>
                                        <li>
                                            <span className="dropdown-item-text">
                                                <kbd>Home</kbd> الصفحة الأولى
                                            </span>
                                        </li>
                                        <li>
                                            <span className="dropdown-item-text">
                                                <kbd>End</kbd> الصفحة الأخيرة
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Progress bar */}
                    {data && data.length > 0 && (
                        <div className="mb-3">
                            <div className="progress" style={{ height: '6px' }}>
                                <div
                                    className="progress-bar bg-primary"
                                    role="progressbar"
                                    style={{
                                        width: `${totalPosts > 0 ? (data.length / totalPosts) * 100 : 0}%`,
                                    }}
                                    aria-valuenow={data.length}
                                    aria-valuemin="0"
                                    aria-valuemax={totalPosts || 0}
                                ></div>
                            </div>
                            <small className="text-muted">
                                {selectedCategory
                                    ? `تم تحميل ${data.length} من ${totalPosts || '...'} ${pagetitle} في ${selectedCategory.name}`
                                    : `تم تحميل ${data.length} من ${totalPosts || '...'} مشاركة`}
                            </small>
                        </div>
                    )}

                    {showAddNew && <AddNewPost postTypes={[postType]} />}

                    <ul className="list-group">
                        {data.map((item, index) => (
                            <li
                                key={item.id}
                                className="list-group-item my-3 border-0 bg-light rounded-3 px-0 py-3"
                                ref={
                                    useInfiniteScroll && index === data.length - 1
                                        ? lastPostElementRef
                                        : null
                                }
                            >
                                <Link href={`design-reviews/${item.id}`}>
                                    {/* {JSON.stringify(item)} */}
                                    <div className="row mx-0">
                                        {item.featured_image_urls && (
                                            <div className="col-4 px-0 ps-md-3 bg-light">
                                                <img
                                                    src={item.featured_image_urls.medium}
                                                    alt={item.title.rendered}
                                                    className="img-fluid w-100 rounded"
                                                />
                                            </div>
                                        )}
                                        <div className="col-8 justify-content-between align-items-center pe-0">
                                            <b
                                                className="d-block text-decoration-none"
                                                dangerouslySetInnerHTML={{
                                                    __html: item.title.rendered,
                                                }}
                                            ></b>
                                            <span className="d-block mt-2 fs-6 text-decoration-none">
                                                {' '}
                                                <i className="bi bi-calendar"></i>{' '}
                                                {formatArabicDate(item.date)}
                                            </span>
                                            <p
                                                className="fs-6 d-none d-md-block my-2"
                                                dangerouslySetInnerHTML={{
                                                    __html: truncateText(item.excerpt.rendered, 200),
                                                }}
                                            ></p>
                                            <AuthorInfo authorId={item.author} postType={postType} />

                                            {/* <span className="btn btn-dark">تابع القراءة <i className='bi bi-chevron-left'></i></span> */}
                                        </div>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {/* Load More Button */}
                    {hasMore &&
                        !loading &&
                        !useInfiniteScroll &&
                        data &&
                        data.length > 0 && (
                            <div className="text-center mt-4">
                                <button
                                    className="btn btn-outline-dark btn-lg px-4 load-more-btn"
                                    onClick={loadMorePosts}
                                    disabled={loading}
                                >
                                    <i className="bi bi-arrow-down-circle me-2"></i>
                                    {selectedCategory
                                        ? `تحميل المزيد من ${pagetitle} في ${selectedCategory.name}`
                                        : `تحميل المزيد من ${pagetitle}`}
                                </button>
                            </div>
                        )}

                    {/* Loading indicator */}
                    {loading && (
                        <div className="text-center mt-4">
                            <div
                                className="spinner-border text-dark loading-spinner"
                                role="status"
                            >
                                <span className="visually-hidden">جاري التحميل...</span>
                            </div>
                            <p className="mt-2 text-muted">
                                {selectedCategory
                                    ? `جاري تحميل ${pagetitle} في ${selectedCategory.name} ...`
                                    : `جاري تحميل ${pagetitle} ...`}
                            </p>
                        </div>
                    )}

                    {/* No more posts message */}
                    {!hasMore && data && data.length > 0 && (
                        <div className="text-center mt-4 p-3 bg-light rounded-3">
                            <i className="bi bi-check-circle text-success fs-1"></i>
                            <p className="mt-2 text-muted mb-0">
                                {selectedCategory
                                    ? `تم تحميل جميع ${pagetitle} في ${selectedCategory.name}`
                                    : `تم تحميل جميع ${pagetitle}`}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
